import {
  type TraderDBAccount,
  type ProductSpecification, type TradableSymbol,
} from '@qep/types/src';
import {Searchfield} from '@qep/shared/src';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {LoadingIndicator} from '../loading-indicator/loading-indicator.js';
import {
  type AppDispatch, type DefaultPreferencesState, getDefaults, getAccounts, type ProductDefaultChange, updateProductDefault,
} from './store/application-store.js';
import AccountSelectorDropdown from './default-account-selector.js';
import DefaultDisplayQuantitySetter from './default-display-quantity-setter.js';
import DefaultIcebergSetter from './default-iceberg-setter.js';
import DefaultQtySetter from './default-quantity-setter.js';
import './Defaults.scss';

type RootReducer = {
  defaultsPreferencesReducer: DefaultPreferencesState;
};

type ProductWithAccounts = {
  symbol: TradableSymbol;
  accounts: TraderDBAccount[];
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const allProducts: TradableSymbol[] = useSelector((state: RootReducer) => state.defaultsPreferencesReducer.products);
  const accounts: TraderDBAccount[] = useSelector((state: RootReducer) => state.defaultsPreferencesReducer.accounts);
  const hasFetchAccounts: boolean = useSelector((state: RootReducer) => state.defaultsPreferencesReducer.fetchedAccounts);
  const hasFetchedDefaults: boolean = useSelector((state: RootReducer) => state.defaultsPreferencesReducer.fetchedProducts);

  const [searchText, setSearchText] = useState<string>('');
  const [products, setProducts] = useState<ProductWithAccounts[] | undefined>(undefined);
  const [filteredProducts, setFilteredProducts] = useState<ProductWithAccounts[] | undefined>(undefined);

  const isLoading = !hasFetchAccounts || !hasFetchedDefaults;

  useEffect(() => {
    if (!hasFetchedDefaults) {
      void dispatch(getDefaults());
    }

    if (!hasFetchAccounts) {
      void dispatch(getAccounts());
    }
  }, []);

  const getViableAccountsForProduct = (symbol: TradableSymbol) => {
    const viableAccounts: TraderDBAccount[] = [];
    for (const acc of accounts) {
      if (acc.productSpecifications && acc.productSpecifications.length > 0) {
        const prodSpec: ProductSpecification | undefined = acc.productSpecifications.find((spec: ProductSpecification) => spec.exchangeName === symbol.segmentMic && spec.symbol === symbol.rootSymbol);
        if (prodSpec) {
          viableAccounts.push(acc);
        }
      }
    }

    return viableAccounts;
  };

  useEffect(() => {
    const newProducts: ProductWithAccounts[] = [];

    if (allProducts.length > 0 && accounts?.length > 0) {
      for (const product of allProducts) {
        const viableAccounts: TraderDBAccount[] = getViableAccountsForProduct(product);
        if (viableAccounts.length > 0) {
          newProducts.push({
            symbol: product,
            accounts: viableAccounts,
          });
        }
      }
    }

    if (!isLoading) {
      setProducts(newProducts);
    }
  }, [allProducts, accounts, isLoading]);

  useEffect(() => {
    if (searchText !== '') {
      filterProducts();
    }
  }, [searchText]);

  useEffect(() => {
    filterProducts();
  }, [products]);

  const getProductName = (product: TradableSymbol): string => {
    if (product.symbolName) {
      return `${product.symbolName} - (${product.rootSymbol})`;
    }

    return `${product.rootSymbol}\\${product.segmentMic}`;
  };

  /** Dispatches one ordered save and consumes the intentionally UI-silent rejection. */
  const saveProductDefaultChange = (change: ProductDefaultChange) => {
    // Event handlers cannot await persistence, so consume failures while the queue releases itself.
    // eslint-disable-next-line promise/prefer-await-to-then
    void dispatch(updateProductDefault(change)).catch(() => undefined);
  };

  /** Queues an account change without capturing the row's other potentially stale fields. */
  const setAccountOnProduct = (value: TraderDBAccount | undefined, sym: TradableSymbol) => {
    if (allProducts.length > 0) {
      saveProductDefaultChange({
        kind: 'account',
        product: `${sym.rootSymbol}\\${sym.segmentMic}`,
        value: value?.id ?? '',
      });
    }
  };

  /** Queues a valid quantity change while preserving all fields accepted by earlier saves. */
  const setQtyOnProduct = (value: number, sym: TradableSymbol) => {
    if (allProducts.length > 0) {
      const newQty: number = value;

      // Always save if we have a valid number, even if it's the same (to ensure persistence)
      if (!Number.isNaN(newQty)) {
        saveProductDefaultChange({
          kind: 'quantity',
          product: `${sym.rootSymbol}\\${sym.segmentMic}`,
          value: newQty,
        });
      }
    }
  };

  /** Queues an iceberg toggle and lets the store derive its display-quantity semantics. */
  const setIcebergOnProduct = (value: boolean, sym: TradableSymbol) => {
    if (allProducts.length > 0) {
      saveProductDefaultChange({
        kind: 'iceberg',
        product: `${sym.rootSymbol}\\${sym.segmentMic}`,
        value,
      });
    }
  };

  /** Queues a non-negative iceberg display quantity for the selected product. */
  const setDisplayQtyOnProduct = (value: number, sym: TradableSymbol) => {
    if (allProducts.length > 0) {
      const newQty = Number(value);
      if (!Number.isNaN(newQty) && newQty >= 0) {
        saveProductDefaultChange({
          kind: 'displayQuantity',
          product: `${sym.rootSymbol}\\${sym.segmentMic}`,
          value: newQty,
        });
      }
    }
  };

  const clearFilter = () => {
    setSearchText('');
    setFilteredProducts(products);
  };

  const filterProducts = () => {
    const search: string = searchText.toLowerCase();
    if (search === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products?.filter((entry: ProductWithAccounts) => {
        const product: TradableSymbol = entry.symbol;
        const productNameInTable: string = getProductName(product);
        return (
          productNameInTable.toLowerCase().includes(search)
          || product.rootSymbol.toLowerCase().includes(search)
          || product.segmentMic.toLowerCase().includes(search)
          || (product.symbolName?.toLowerCase().includes(search))
        );
      });

      setFilteredProducts(filtered);
    }
  };

  return (
    <div className='product-default-content'>
      {isLoading || (products === null)
        ? (
          <LoadingIndicator />
        )
        : (
          (products !== undefined && products.length <= 0)
            ? (
              <div className='no-results'>No tradable products available.</div>
            )
            : (
              <>
                <div className='search-container'>
                  <Searchfield
                    placeholder='Search for product'
                    onChange={value => {
                      if (value === '') {
                        clearFilter();
                      }

                      setSearchText(value);
                    }}
                    onClear={() => {/* no-op */}}
                  />
                </div>
                <table className='header-table'>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Default Quantity</th>
                      <th>Iceberg</th>
                      <th>Display Quantity</th>
                      <th>Default Strategy</th>
                    </tr>
                  </thead>
                </table>
                <div className='product-list'>
                  {filteredProducts?.length === 0 && <div className='no-results'>There are no products matching this search</div>}
                  {(filteredProducts && filteredProducts.length > 0) && <div className='product-table'>
                    <table>
                      <tbody>
                        {filteredProducts.map((entry: ProductWithAccounts) => {
                          const product = entry.symbol;
                          return (
                            <tr key={`${product.rootSymbol}\\${product.segmentMic}`}>
                              <td><span className='product-name'>{getProductName(product)}</span></td>
                              <td>
                                <div className='product-qty'>
                                  <DefaultQtySetter
                                    tradableSymbol={product}
                                    handleChange={setQtyOnProduct}
                                  />
                                </div>
                              </td>
                              <td>
                                <DefaultIcebergSetter
                                  tradableSymbol={product}
                                  handleChange={setIcebergOnProduct}
                                />
                              </td>
                              <td>
                                <DefaultDisplayQuantitySetter
                                  tradableSymbol={product}
                                  handleChange={setDisplayQtyOnProduct}
                                />
                              </td>
                              <td>
                                <div className='product-account'>
                                  <AccountSelectorDropdown
                                    tradableSymbol={product}
                                    accounts={entry.accounts}
                                    handleChange={setAccountOnProduct}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>}
                </div>
              </>
            )
        )}
    </div>
  );
};

export default App;
